"use client";

import { X } from "lucide-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

const chipBodyButtonClass =
  "inline-flex cursor-pointer items-center gap-1.5 transition-[background-color,color,transform] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100";

export type RemovableChipProps = {
  /** Accessible label for the trailing remove (×) button. */
  removeLabel: string;
  /** Remove handler for the trailing × button. */
  onRemove?: () => void;
  /**
   * Pressed/open visual — adds a ring so the active state reads at a glance.
   * The ring uses the default focus colour; override it (or add a fill) through
   * `className` when a chip wants a branded active state.
   */
  active?: boolean;
  /**
   * Chip body — typically one or more `RemovableChipButton`s plus any middle
   * affordances (e.g. a popover trigger). The faint divider and the trailing ×
   * button are appended automatically.
   */
  children: React.ReactNode;
} & Omit<React.ComponentProps<"div">, "children">;

/**
 * The shared shell for the picker's segmented chips: a rounded `bg-muted/40`
 * pill hosting a body, then a faint divider, then a trailing × remove button.
 * Centralising the shell, divider, and remove button keeps every chip's shape,
 * focus ring, and press animation in sync — consumers supply only the body and
 * the labels. Powers `McpToolkitChip` and `McpRecommendedToolkitsChip`.
 */
export function RemovableChip({
  removeLabel,
  onRemove,
  active = false,
  children,
  className,
  ...rest
}: RemovableChipProps) {
  return (
    <div
      className={cn(
        "ds-focus-in inline-flex shrink-0 items-center rounded-md bg-muted/40 text-xs",
        active ? "ring-2 ring-ring/60" : "hover:bg-muted/60",
        className
      )}
      {...rest}
    >
      {children}
      {onRemove ? (
        <>
          <span aria-hidden className="h-4 w-px shrink-0 bg-foreground/10" />
          <button
            aria-label={removeLabel}
            className="inline-flex cursor-pointer items-center self-stretch rounded-r-md pr-2 pl-1.5 text-muted-foreground transition-[background-color,color,transform] duration-[var(--duration-fast)] ease-[var(--ease-smooth)] hover:bg-foreground/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100"
            onClick={onRemove}
            title={removeLabel}
            type="button"
          >
            <X aria-hidden className="size-3" />
          </button>
        </>
      ) : null}
    </div>
  );
}

export type RemovableChipButtonProps = Omit<
  React.ComponentProps<"button">,
  "type"
>;

/**
 * A body affordance inside a `RemovableChip` (e.g. "open this server's tools"
 * or "review this set"). Shares the chip's hover, focus, and press styling and
 * rounds its left edge to meet the shell. Pass `className` to tune padding for
 * its position in the chip.
 */
export function RemovableChipButton({
  className,
  ...props
}: RemovableChipButtonProps) {
  return (
    <button
      className={cn(
        chipBodyButtonClass,
        "rounded-l-md py-1 pr-2 pl-1.5",
        className
      )}
      type="button"
      {...props}
    />
  );
}

export { chipBodyButtonClass as removableChipButtonClass };
